import { SyncIcon } from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import { AlertDialog } from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

interface CatalogListActionConfirmation {
  cancelLabel: string;
  confirmLabel: string;
  description: string;
  onConfirm: () => void;
  title: string;
}

export function CatalogListAction({
  active,
  activeClassName,
  confirmation,
  fullLabel,
  icon,
  onClick,
  pending = false,
  shortLabel,
}: {
  active: boolean;
  activeClassName: string;
  confirmation?: CatalogListActionConfirmation;
  fullLabel: string;
  icon: AppIcon;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  pending?: boolean;
  shortLabel: string;
}) {
  const Icon = pending ? SyncIcon : icon;
  const action = (
    <button
      type="button"
      aria-label={fullLabel}
      aria-pressed={active}
      title={fullLabel}
      onClick={confirmation ? undefined : onClick}
      disabled={pending}
      aria-busy={pending}
      className={cn(
        "flex h-11 min-w-0 items-center justify-center gap-1.5 rounded-lg border border-border bg-secondary px-2 text-sm font-bold transition-colors hover:bg-secondary/80 disabled:cursor-wait disabled:opacity-70",
        active && activeClassName,
      )}
    >
      <Icon className={cn("shrink-0 text-lg", pending && "animate-spin")} />
      {/* Always the short verb ("Tenho" / "Quero"); the full phrase stays the accessible name. */}
      <span className="min-w-0 truncate">{shortLabel}</span>
    </button>
  );

  if (!confirmation) return action;

  return (
    <AlertDialog.Root>
      <AlertDialog.Trigger
        render={action}
        onClick={(event) => {
          event.stopPropagation();
        }}
      />
      <AlertDialog.Portal>
        <AlertDialog.Backdrop />
        <AlertDialog.Popup>
          <AlertDialog.Title className="text-lg font-bold">{confirmation.title}</AlertDialog.Title>
          <AlertDialog.Description className="mt-2 text-sm text-muted-foreground">
            {confirmation.description}
          </AlertDialog.Description>
          <div className="mt-6 flex justify-end gap-2">
            <AlertDialog.Close
              data-autofocus
              className="rounded-lg px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary"
            >
              {confirmation.cancelLabel}
            </AlertDialog.Close>
            <AlertDialog.Close
              onClick={confirmation.onConfirm}
              className="rounded-lg bg-destructive/10 px-4 py-2 text-sm font-bold text-destructive transition-colors hover:bg-destructive/20"
            >
              {confirmation.confirmLabel}
            </AlertDialog.Close>
          </div>
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
